import React, { useState, useEffect, useRef } from 'react';

function PhysicsOverlay({ onClose }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    // Gather visible text elements to simulate
    const elements = [];
    document.querySelectorAll('h1, h2, h3, p, span, div, a, button, li').forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.width < 10 || rect.height < 10 || rect.top > H || rect.bottom < 0) return;
      if (rect.width > W * 0.8) return; // skip full-width containers
      const text = el.textContent?.slice(0, 30) || '';
      if (!text.trim()) return;
      elements.push({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        origX: rect.left + rect.width / 2,
        origY: rect.top + rect.height / 2,
        w: Math.min(rect.width, 300),
        h: rect.height,
        vx: (Math.random() - 0.5) * 8,
        vy: -Math.random() * 6 - 2,
        text: text,
        fontSize: Math.min(parseInt(window.getComputedStyle(el).fontSize) || 14, 24),
        phase: 'fall', // fall, then reassemble
        rotation: 0,
        vr: (Math.random() - 0.5) * 0.08,
      });
    });

    // Limit to 60 elements for performance
    const particles = elements.slice(0, 60);
    const gravity = 0.35;
    const bounce = 0.5;
    let phase = 'fall';
    let timer = 0;

    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      timer++;

      // Switch to reassemble after 120 frames (~2 seconds)
      if (timer === 120) phase = 'reassemble';

      particles.forEach(p => {
        if (phase === 'fall') {
          p.vy += gravity;
          p.x += p.vx;
          p.y += p.vy;
          p.rotation += p.vr;

          // Bounce off floor
          if (p.y + p.h / 2 > H) {
            p.y = H - p.h / 2;
            p.vy *= -bounce;
            p.vx *= 0.9;
          }
          // Bounce off walls
          if (p.x < 0 || p.x > W) p.vx *= -1;
        } else {
          // Reassemble — lerp back
          p.x += (p.origX - p.x) * 0.06;
          p.y += (p.origY - p.y) * 0.06;
          p.rotation *= 0.92;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.font = `300 ${p.fontSize}px 'DM Sans', sans-serif`;
        ctx.fillStyle = phase === 'fall' ? 'rgba(250,250,248,0.7)' : 'rgba(250,250,248,0.5)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(p.text, 0, 0);
        ctx.restore();
      });

      // Close after reassembly
      if (timer > 240) {
        onClose();
        return;
      }

      requestAnimationFrame(animate);
    };

    animate();
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#0a0a0a' }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      <div style={{
        position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)',
        fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em',
        color: 'rgba(250,250,248,0.3)', textTransform: 'uppercase',
      }}>
        {' '}Physics Mode — Reassembling...
      </div>
    </div>
  );
}

export default function PhysicsMode() {
  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <>
      {/* E=mc² Orb — fixed position, small, unlabeled */}
      <div
        onClick={() => setActive(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          position: 'fixed', bottom: '180px', right: '30px', zIndex: 700,
          width: '20px', height: '20px', borderRadius: '50%',
          background: 'radial-gradient(circle at 30% 30%, #c084fc, #6d28d9)',
          cursor: 'crosshair',
          boxShadow: hovered
            ? '0 0 20px rgba(192,132,252,0.5)'
            : '0 0 8px rgba(192,132,252,0.2)',
          transition: 'box-shadow 0.3s, transform 0.3s',
          transform: hovered ? 'scale(1.2)' : 'scale(1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: '6px', color: 'rgba(250,250,248,0.8)',
          fontWeight: 500, letterSpacing: '0.05em',
        }}>
          E
        </span>
      </div>

      {active && <PhysicsOverlay onClose={() => setActive(false)} />}
    </>
  );
}
